/**
 * The card grid: equal columns, arrangeable when unlocked.
 *
 * Drag here is two-dimensional, which is the one real difference from `useRowReorder`. That
 * hook resolves a pointer to a single number — the gap between rows. A card has to resolve
 * to a column *and* a gap, and the pointer can also be over a column with no cards in it at
 * all, which no amount of row arithmetic covers. So the hit test asks two questions: which
 * column am I over, and where in it.
 *
 * The pointer-capture and `elementFromPoint` approach is lifted wholesale from that hook,
 * because it is the part that makes touch work, and having two spellings of it would be one
 * too many.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useEditMode } from "./editModeContext";
import { MAX_MANUAL_COLUMNS, useColumns } from "./useColumns";
import { navigate } from "../navigation";
import {
  BUILTIN_PAGES,
  PORTABLE_SECTIONS,
  SECTION_LABELS,
  type Arrangement,
  type PageId,
  type SectionId,
  type Widths,
  addSectionToPage,
  clearArrangement,
  clearSectionPlacement,
  defaultWidths,
  isSectionOnPage,
  loadArrangement,
  loadColumnOverride,
  loadWidths,
  moveSection,
  removeSectionFromPage,
  resetPages,
  resizeColumns,
  saveArrangement,
  saveColumnOverride,
  saveWidths,
  sectionsForPage,
  seedExplicitWidths,
} from "./model";

export type Section = { id: SectionId; node: React.ReactNode };

type Target = { column: number; gap: number };

/**
 * An in-flight gutter drag: which boundary, and what the widths were when it started.
 * `minLeft`/`minRight` carry an edge drag's relaxed floor (a margin can close to nothing;
 * see `resizeColumns`) through to `onPointerMove`, which only has `resizing` to read from.
 *
 * `kind` disambiguates a boundary number that briefly means two different things: before
 * the first edge drag (still cap mode, no margin tracks yet), an edge handle and the
 * internal handle nearest it both compute boundary `0` — the edge because it always does,
 * the internal one because there is no margin track ahead of it yet to shift it off `0`.
 * Without `kind`, whichever handle you were not dragging would still light up as active.
 */
type Resizing = {
  boundary: number;
  kind?: "start" | "end";
  startX: number;
  startWidths: Widths;
  minLeft?: number;
  minRight?: number;
};

/** Which gap in a column a pointer at this height is aiming at. */
function gapAt(index: number, box: DOMRect, clientY: number): number {
  return clientY < box.top + box.height / 2 ? index : index + 1;
}

export function LayoutGrid({
  page,
  sections,
  onSectionsChanged,
}: {
  page: PageId;
  sections: Section[];
  /**
   * Called after `AddSectionPicker` moves a section onto this page (stage 5), so the parent
   * — the one that actually computes `sections` from `sectionsForPage` — knows to
   * recompute it. Without this the moved card only shows as a placeholder until something
   * else happens to re-render the parent; with it, locking the page immediately shows the
   * real thing. Optional because not every caller needs the picker at all (`editing` gates
   * it), but every page that renders `LayoutGrid` today happens to already have a
   * `revision`/`bump` pair handy to pass — see D114.
   */
  onSectionsChanged?: () => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const automatic = useColumns(container);
  const { editing } = useEditMode();

  const addHere = useCallback(
    (id: SectionId) => {
      addSectionToPage(id, page);
      onSectionsChanged?.();
    },
    [page, onSectionsChanged],
  );

  const removeHere = useCallback(
    (id: SectionId) => {
      removeSectionFromPage(id, page);
      onSectionsChanged?.();
    },
    [page, onSectionsChanged],
  );

  // A hand-set count wins over the measured one. Null means "follow the width", which is
  // what most people will leave it on and what a new device starts with.
  const [override, setOverride] = useState<number | null>(() => loadColumnOverride(page));
  const columns = override ?? automatic;

  const chooseColumns = useCallback(
    (next: number | null) => {
      setOverride(next);
      saveColumnOverride(page, next);
    },
    [page],
  );

  /*
    While arranging, every card the page can hold is placeable - including the ones that are
    not currently showing. A conditional card you cannot see is a card you cannot position,
    and the alternative is arranging for something to land in the inbox purely so you can
    decide where the inbox goes.

    Absent ones render as placeholders (below) and their positions are stored like any
    other, so when the real card arrives it appears where you put it.
  */
  const shown = new Set(sections.map((section) => section.id));
  const placed: Section[] = editing
    ? sectionsForPage(page).map(
        (id) =>
          sections.find((section) => section.id === id) ?? {
            id,
            node: <Placeholder id={id} />,
          },
      )
    : sections;

  const present = placed.map((section) => section.id);
  // Kept as a string so the effect below re-runs when a card appears or disappears — a
  // conditional section arriving has to find a home, and that is `reconcile`'s job.
  const presentKey = present.join(",");

  const [arrangement, setArrangement] = useState<Arrangement>(() =>
    loadArrangement(page, columns, present),
  );
  const [dragging, setDragging] = useState<SectionId | null>(null);
  const [target, setTarget] = useState<Target | null>(null);
  const [widths, setWidths] = useState<Widths>(() => loadWidths(page, columns));
  const [resizing, setResizing] = useState<Resizing | null>(null);

  useEffect(() => {
    setArrangement(loadArrangement(page, columns, presentKey.split(",") as SectionId[]));
  }, [page, columns, presentKey]);

  useEffect(() => {
    setWidths(loadWidths(page, columns));
  }, [page, columns]);

  const apply = useCallback(
    (id: SectionId, to: Target) => {
      setArrangement((current) => {
        const next = moveSection(current, id, to.column, to.gap);
        saveArrangement(page, columns, next);
        return next;
      });
    },
    [page, columns],
  );

  const reset = useCallback(() => {
    setDragging(null);
    setTarget(null);
  }, []);

  const commit = useCallback(() => {
    if (dragging && target) apply(dragging, target);
    reset();
  }, [dragging, target, apply, reset]);

  const locate = useCallback((clientX: number, clientY: number): Target | null => {
    const element = document.elementFromPoint(clientX, clientY);
    if (!element) return null;

    const slot = element.closest<HTMLElement>("[data-layout-slot]");
    if (slot?.dataset.column && slot.dataset.index) {
      return {
        column: Number(slot.dataset.column),
        gap: gapAt(Number(slot.dataset.index), slot.getBoundingClientRect(), clientY),
      };
    }

    // Over a column but not over a card: an empty column, or the space past the last one.
    const column = element.closest<HTMLElement>("[data-layout-column]");
    if (column?.dataset.layoutColumn) {
      const index = Number(column.dataset.layoutColumn);
      return {
        column: index,
        gap: column.querySelectorAll("[data-layout-slot]").length,
      };
    }

    return null;
  }, []);

  const handleProps = (id: SectionId, column: number, index: number) => ({
    "aria-label": `Move ${id} - drag, or use the arrow keys`,
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      if (event.pointerType === "mouse" && event.button !== 0) return;
      // Without this a touch drag scrolls the page. `touch-action: none` on the handle is
      // the other half; neither works alone.
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      setDragging(id);
      setTarget({ column, gap: index });
    },
    onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
      if (!dragging) return;
      const found = locate(event.clientX, event.clientY);
      if (found) setTarget(found);
    },
    onPointerUp: commit,
    onPointerCancel: reset,
    onLostPointerCapture: reset,

    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
      const moves: Record<string, Target | undefined> = {
        ArrowUp: { column, gap: index - 1 },
        ArrowDown: { column, gap: index + 2 },
        ArrowLeft: column > 0 ? { column: column - 1, gap: index } : undefined,
        ArrowRight: column < columns - 1 ? { column: column + 1, gap: index } : undefined,
      };
      const move = moves[event.key];
      if (!move) return;
      event.preventDefault();
      apply(id, move);
    },
  });

  /**
   * Drag a gutter. The delta is a fraction of the container, so the same gesture means the
   * same thing on a phone and an ultrawide, and a stored width survives a resize.
   *
   * `edge`, when passed, is the outer-edge case: the first drag of an edge that has never
   * been touched has to convert `widths` from cap mode (CSS supplies the margin) to
   * explicit mode (the margin is a track, seeded from whatever is on screen right now) —
   * see `seedExplicitWidths`. Its `minLeft`/`minRight` is whichever side of the pair is the
   * margin, so that side alone can be dragged fully shut.
   */
  const resizerProps = (
    boundary: number,
    edge?: { kind: "start" | "end"; minLeft?: number; minRight?: number; label: string },
  ) => {
    const start = (): Widths => {
      if (!edge) return widths;
      const seeded = seedExplicitWidths(widths, columns, container.current);
      if (seeded !== widths) setWidths(seeded);
      return seeded;
    };

    return {
      "aria-label": edge?.label ?? `Resize column ${boundary + 1}`,
      onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        setResizing({
          boundary,
          kind: edge?.kind,
          startX: event.clientX,
          startWidths: start(),
          minLeft: edge?.minLeft,
          minRight: edge?.minRight,
        });
      },
      onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
        if (!resizing || resizing.boundary !== boundary || resizing.kind !== edge?.kind)
          return;
        const box = container.current?.getBoundingClientRect();
        if (!box || box.width === 0) return;
        setWidths(
          resizeColumns(
            resizing.startWidths,
            boundary,
            (event.clientX - resizing.startX) / box.width,
            resizing.minLeft,
            resizing.minRight,
          ),
        );
      },
      onPointerUp: () => {
        if (resizing) saveWidths(page, columns, widths);
        setResizing(null);
      },
      onPointerCancel: () => setResizing(null),
      onLostPointerCapture: () => setResizing(null),
      onDoubleClick: () => {
        // The way back to even, without hunting for a reset button. Back to cap mode too —
        // defaultWidths is the `columns`-length shape, whatever mode was current before.
        const even = defaultWidths(columns);
        setWidths(even);
        saveWidths(page, columns, even);
      },
      onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
        const step = event.key === "ArrowLeft" ? -0.02 : event.key === "ArrowRight" ? 0.02 : 0;
        if (!step) return;
        event.preventDefault();
        const next = resizeColumns(start(), boundary, step, edge?.minLeft, edge?.minRight);
        setWidths(next);
        saveWidths(page, columns, next);
      },
    };
  };

  const byId = new Map(placed.map((section) => [section.id, section.node]));

  // Explicit mode: an outer edge has been dragged, so `widths` carries a margin at each
  // end and real columns start one track later. Cap mode (the untouched default) has
  // neither, and the CSS cap + auto-margin supplies the outer space instead.
  const explicit = widths.length === columns + 2;
  const marginOffset = explicit ? 1 : 0;

  return (
    /*
      The measured element is not the styled one, and that separation is load-bearing.

      `.layout-grid.single` caps a one-column layout at a reading measure. With the ref on
      the grid itself, that cap became the *input* to the measurement that produced it: drop
      to one column for any reason and the element shrinks to 900px, the observer measures
      900px, and 900px is not enough for two columns - so it stays at one, permanently. It
      latched on the very first paint, because the count starts at 1 before anything has
      been measured.

      This wrapper is never styled by the result, so the measurement is always of the space
      actually available.
    */
    <div ref={container} className="layout-measure" data-layout-measure>
      {editing && (
        <ColumnPicker
          value={override}
          automatic={automatic}
          onChange={chooseColumns}
          page={page}
          onAddSection={addHere}
          onRemoveSection={removeHere}
          onReset={() => {
            if (
              window.confirm(
                "Put every card back where it started?\n\n" +
                  "This page's widths and column count reset here only. Which page every " +
                  "card lives on, and the page list itself, reset everywhere - any page you " +
                  "created is gone, any page you deleted is back, and every card returns to " +
                  "its default page. Only this device, not synced anywhere.",
              )
            ) {
              clearArrangement(page);
              clearSectionPlacement();
              resetPages();
              // A custom page cannot survive a page-list reset — it just went away — so
              // there is nothing left here to stay on. `/` always resolves to Briefing
              // (`routes.ts`'s fixed paths), which the reset page list is guaranteed to
              // include, unlike whatever id this custom page happened to have.
              if (!BUILTIN_PAGES.includes(page)) navigate("/");
              // Not `null` — that's "auto" specifically, which is only this page's default
              // when nothing else is (Briefing). Re-reading after clearing gets whichever
              // this page actually resets to (Structure and Settings have their own).
              const fresh = loadColumnOverride(page);
              const freshColumns = fresh ?? automatic;
              setOverride(fresh);
              setWidths(defaultWidths(freshColumns));
              setArrangement(loadArrangement(page, freshColumns, sectionsForPage(page)));
              onSectionsChanged?.();
            }
          }}
        />
      )}
      <div
        className={`layout-grid${columns === 1 ? " single" : ""}${
          explicit ? " explicit-widths" : ""
        }${editing ? " arranging" : ""}${dragging ? " dragging" : ""}${
          resizing ? " resizing" : ""
        }`}
        style={
          {
            gridTemplateColumns: widths.map((w) => `minmax(0, ${w}fr)`).join(" "),
            // Read by the stylesheet to cap the grid at a sensible width *per column*,
            // rather than at one width however many columns there are. Meaningless once
            // `explicit-widths` suppresses the cap, but harmless left set.
            "--layout-columns": columns,
          } as React.CSSProperties
        }
      >
        {arrangement.slice(0, columns).map((column, columnIndex) => (
          <div
            key={columnIndex}
            className="layout-column"
            data-layout-column={columnIndex}
            // In cap mode there is no margin track, so track `columnIndex + 1` (grid lines
            // are 1-based) is this column already — same as leaving placement to auto-flow.
            // In explicit mode the margin track sits before it, hence `+ marginOffset`.
            style={{ gridColumn: columnIndex + 1 + marginOffset }}
          >
            {/*
              The left edge of the *first* column only: grow into the left margin instead
              of shrinking a real neighbour. Independent of the internal handles below -
              a single-column page has both this and the right-edge handle on the same
              column, since it is simultaneously the first and the last.
            */}
            {editing && columnIndex === 0 && (
              <button
                type="button"
                className={`col-resizer edge-start${
                  resizing?.boundary === 0 && resizing.kind === "start" ? " active" : ""
                }`}
                title="Drag to grow into the margin · double-click to reset · arrow keys to nudge"
                {...resizerProps(0, {
                  kind: "start",
                  minLeft: 0,
                  label: "Resize left margin",
                })}
              >
                <span className="sr-only">Resize left margin</span>
              </button>
            )}
            {/*
              The gutter handle, on every column but the last. It lives inside the column and
              hangs off its right edge, so it needs no absolute arithmetic against the grid -
              the column already knows where its own edge is.
            */}
            {editing && columnIndex < columns - 1 && (
              <button
                type="button"
                className={`col-resizer${
                  resizing?.boundary === columnIndex + marginOffset && !resizing.kind
                    ? " active"
                    : ""
                }`}
                title="Drag to resize · double-click to even them up · arrow keys to nudge"
                {...resizerProps(columnIndex + marginOffset)}
              >
                <span className="sr-only">Resize column {columnIndex + 1}</span>
              </button>
            )}
            {/* The right edge of the *last* column: grow into the right margin. */}
            {editing && columnIndex === columns - 1 && (
              <button
                type="button"
                className={`col-resizer edge-end${
                  resizing?.boundary === columns && resizing.kind === "end" ? " active" : ""
                }`}
                title="Drag to grow into the margin · double-click to reset · arrow keys to nudge"
                {...resizerProps(columns, {
                  kind: "end",
                  minRight: 0,
                  label: "Resize right margin",
                })}
              >
                <span className="sr-only">Resize right margin</span>
              </button>
            )}
            {column.map((id, index) => {
              const node = byId.get(id);
              if (!node) return null;

              const isTarget =
                target?.column === columnIndex &&
                (target.gap === index ||
                  (target.gap >= column.length && index === column.length - 1));
              const edge = !isTarget
                ? ""
                : target.gap === index
                  ? " drop-top"
                  : " drop-bottom";

              return (
                <div
                  key={id}
                  data-layout-slot={id}
                  data-column={columnIndex}
                  data-index={index}
                  className={`layout-slot${dragging === id ? " lifted" : ""}${edge}${
                    shown.has(id) ? "" : " absent"
                  }`}
                >
                  {editing && (
                    <button
                      type="button"
                      className="card-grip"
                      title="Drag to move this card, or use the arrow keys"
                      {...handleProps(id, columnIndex, index)}
                    >
                      <span className="sr-only">Move {id}</span>
                    </button>
                  )}
                  {node}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * How many columns, while arranging.
 *
 * `auto` is the default and follows the width. The manual ceiling is deliberately higher
 * than sensible: eight columns on a laptop is a mess, and one click undoes it. A limit set
 * at what someone else thinks you should want is the worse failure.
 */
function ColumnPicker({
  value,
  automatic,
  onChange,
  page,
  onAddSection,
  onRemoveSection,
  onReset,
}: {
  value: number | null;
  automatic: number;
  onChange: (next: number | null) => void;
  page: PageId;
  onAddSection: (id: SectionId) => void;
  onRemoveSection: (id: SectionId) => void;
  onReset: () => void;
}) {
  const shown = value ?? automatic;

  return (
    <div className="column-picker" role="group" aria-label="Columns">
      <span className="small muted">Columns</span>

      <button
        type="button"
        className="btn ghost small"
        onClick={() => onChange(Math.max(shown - 1, 1))}
        disabled={shown <= 1}
        aria-label="One fewer column"
        title="One fewer"
      >
        −
      </button>

      <span className="count" aria-live="polite">
        {shown}
        {value === null && <span className="small muted"> auto</span>}
      </span>

      <button
        type="button"
        className="btn ghost small"
        onClick={() => onChange(Math.min(shown + 1, MAX_MANUAL_COLUMNS))}
        disabled={shown >= MAX_MANUAL_COLUMNS}
        aria-label="One more column"
        title="One more"
      >
        +
      </button>

      <button
        type="button"
        className={`btn small${value === null ? " primary" : " ghost"}`}
        onClick={() => onChange(null)}
        aria-pressed={value === null}
        title="Follow the window width"
      >
        auto
      </button>

      <CardsPicker page={page} onAdd={onAddSection} onRemove={onRemoveSection} />

      <button
        type="button"
        className="btn ghost small"
        onClick={onReset}
        title="Put every card back where it started - this page's layout, and every page's card placement"
      >
        reset all
      </button>
    </div>
  );
}

/**
 * Stage 5, redone as checkboxes: every portable card, checked if it is on this page. A
 * `<select>` version existed first, listing only cards *not* already here and moving one in
 * on selection — the small × next to each card's grip (D115) was the only way back off. A
 * single checkbox list replaces both: check to add, uncheck to remove, no tap target
 * smaller than an ordinary row. `<details>`/`<summary>` for the disclosure rather than
 * hand-rolled open/close state — free keyboard and touch behavior, and it sizes to its own
 * content instead of stretching to fill the toolbar.
 */
function CardsPicker({
  page,
  onAdd,
  onRemove,
}: {
  page: PageId;
  onAdd: (id: SectionId) => void;
  onRemove: (id: SectionId) => void;
}) {
  // The panel is only rendered while open, rather than always in the DOM and CSS-hidden by
  // `<details>` natively — a closed panel's own text (every card's label) would otherwise
  // still be findable by a loose text query, colliding with the placeholder that shows the
  // same label when a card isn't currently on screen.
  const [open, setOpen] = useState(false);

  return (
    <details
      className="cards-picker"
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="btn ghost small" role="button" aria-expanded={open}>
        Cards
      </summary>
      {open && (
        <div className="cards-picker-panel" role="group" aria-label="Cards on this page">
          {PORTABLE_SECTIONS.map((id) => (
            <label key={id} className="cards-picker-row">
              <input
                type="checkbox"
                checked={isSectionOnPage(id, page)}
                onChange={(event) => {
                  if (event.target.checked) onAdd(id);
                  else onRemove(id);
                }}
              />
              {SECTION_LABELS[id]}
            </label>
          ))}
        </div>
      )}
    </details>
  );
}

/**
 * A card that is not currently showing, so that it can still be positioned.
 *
 * Exported for self-contained cards that can legitimately render nothing (`NoticeCard`,
 * `FirstRunCard`) to use directly: `renderSection` hands back a real element the moment an id
 * is registered, whether or not the card underneath decides there's anything to show, so this
 * component's own "absent" fallback (below) never gets a chance to run for them — a truthy
 * `<NoticeCard/>` element is not the same thing as `NoticeCard` having rendered content. The
 * card is the only thing that knows which case it's in, so it has to make the call itself.
 */
export function Placeholder({ id }: { id: SectionId }) {
  return (
    <section className="panel placeholder">
      <strong>{SECTION_LABELS[id]}</strong>
      <p className="small muted">
        Not showing right now. Put it where you want it and it will appear here when there is
        something to show.
      </p>
    </section>
  );
}
